import { CheckStatus, DEFAULT_ACTIVE_COLUMNS, MODULE } from '@gearbelt/global';
import { ColumnDef, ColumnFiltersState, GroupingState, VisibilityState } from '@tanstack/react-table';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { columns as assetDocumentColumns } from '@/pages/assets/[id]/documents/table-meta';
import { columns as assetInspectionsColumns } from '@/pages/assets/[id]/inspections/table-meta';
import { createAssetIssueTableColumns } from '@/pages/assets/[id]/issues/table-meta';
import { columns as assetLocationLogsColumns } from '@/pages/assets/[id]/location-logs/table-meta';
import { columns as assetReadingLogsColumns } from '@/pages/assets/[id]/reading-logs/table-meta';
import { columns as assetRepairsColumns } from '@/pages/assets/[id]/repairs/table-meta';
import { createAssetScheduleCheckColumns } from '@/pages/assets/[id]/schedule-checks/table-meta';
import { columns as assetScheduleRecordsColumns } from '@/pages/assets/[id]/schedule-records/table-meta';
import { columns as assetWorkOrderColumns } from '@/pages/assets/[id]/work-orders/table-meta';
import { createAssetTableColumns } from '@/pages/assets/table-meta';
import { createDeploymentTableColumns } from '@/pages/deployment/table-meta';
import { createDocumentsTableColumns } from '@/pages/documents/table-meta';
import { columns as inspectionsColumns } from '@/pages/inspections/activity/table-meta';
import { columns as inspectionSubmissionsColumns } from '@/pages/inspections/forms/[id]/submissions/table-meta';
import { createInspectionFormsTableColumns } from '@/pages/inspections/forms/table-meta';
import { columns as repairsColumns } from '@/pages/issues/repairs/activity/table-meta';
import { columns as repairSubmissionsColumns } from '@/pages/issues/repairs/forms/[id]/submissions/table-meta';
import { columns as repairFormsColumns } from '@/pages/issues/repairs/forms/table-meta';
import { createIssueTableColumns } from '@/pages/issues/table-meta';
import { columns as listsColumns } from '@/pages/lists/table-meta';
import { columns as locationLogsColumns } from '@/pages/locations/location-logs/table-meta';
import { createLocationsTableColumns } from '@/pages/locations/table-meta';
import { columns as scheduleFormColumns } from '@/pages/schedules/activity/table-meta';
import { createCheckTableColumns } from '@/pages/schedules/checks/table-meta';
import { columns as scheduleFormSubmissionsColumns } from '@/pages/schedules/forms/[id]/table-meta';
import { createScheduleFormFormsTableColumns } from '@/pages/schedules/forms/table-meta';
import { createSchedulesTableColumns } from '@/pages/schedules/table-meta';
import { createGearbeltContributorsTableColumns } from '@/pages/users/contributors/table-meta';
import { createSitemateUsersTableColumns } from '@/pages/users/sitemate-users/table-meta';
import { columns as workOrdersColumns } from '@/pages/work-orders/table-meta';

export interface ModuleTableColumn {
  id: string;
  active: boolean;
}

export interface Module {
  handle: MODULE;
  table: {
    sorting: {
      key?: string;
      direction?: 'asc' | 'desc';
    };
    filters?: ColumnFiltersState;
    grouping: GroupingState;
    columnOrder: Array<ModuleTableColumn>;
  };
  view?: 'grid' | 'register';
}

interface ModulesStore {
  modules: Module[];
  getModule: (handle: MODULE) => Module;
  setModule: (handle: MODULE, update: Partial<Omit<Module, 'handle'>>) => void;
  setModuleColumns: (handle: MODULE, columns: Array<ModuleTableColumn>) => void;
  setModuleSorting: (handle: MODULE, sorting: { key?: string; direction?: 'asc' | 'desc' }) => void;
  setModuleGrouping: (handle: MODULE, grouping: string[]) => void;
  setModuleFilters: (handle: MODULE, filters: ColumnFiltersState) => void;
  setModuleView: (handle: MODULE, view: 'grid' | 'register') => void;
  getColumnVisibility: (handle: MODULE) => VisibilityState;
  syncModuleColumns: <T>(handle: MODULE, columns: ColumnDef<T>[]) => void;
}

// Default-active column ids per module live in @gearbelt/global so the API workspace
// seed and this store share one source; a subset drift-guard against the page-config
// column-id tuples lives beside the constant. See .claude/docs/table-column-visibility.md.
export const defaultActiveColumns = DEFAULT_ACTIVE_COLUMNS;

export const defaultModules: ModulesStore['modules'] = [
  {
    handle: MODULE.Assets,
    table: {
      columnOrder: createAssetTableColumns().map((column) => ({
        id: column.id || column.accessorKey,
        active: defaultActiveColumns[MODULE.Assets].includes(column.id || column.accessorKey),
      })),
      grouping: [],
      sorting: {},
    },
  },
  {
    handle: MODULE.Locations,
    table: {
      columnOrder: createLocationsTableColumns().map((column) => ({
        id: column.id || column.accessorKey,
        active: defaultActiveColumns[MODULE.Locations].includes(column.id || column.accessorKey),
      })),
      grouping: [],
      sorting: {},
    },
  },
  {
    handle: MODULE.Lists,
    table: {
      columnOrder: listsColumns.map((column) => ({
        id: column.id || column.accessorKey,
        active: defaultActiveColumns[MODULE.Lists].includes(column.id || column.accessorKey),
      })),
      grouping: [],
      sorting: {},
    },
  },
  {
    handle: MODULE.Inspections,
    table: {
      columnOrder: inspectionsColumns().map((column) => ({
        id: column.id || column.accessorKey,
        active: defaultActiveColumns[MODULE.Inspections].includes(column.id || column.accessorKey),
      })),
      grouping: [],
      sorting: {
        key: 'createdDate',
        direction: 'desc',
      },
    },
  },
  {
    handle: MODULE.InspectionForms,
    table: {
      columnOrder: createInspectionFormsTableColumns().map((column) => ({
        id: column.id || column.accessorKey,
        active: defaultActiveColumns[MODULE.InspectionForms].includes(column.id || column.accessorKey),
      })),
      grouping: [],
      sorting: {},
    },
    view: 'grid',
  },
  {
    handle: MODULE.InspectionSubmissions,
    table: {
      columnOrder: inspectionSubmissionsColumns().map((column) => ({
        id: column.id || column.accessorKey,
        active: defaultActiveColumns[MODULE.InspectionSubmissions].includes(column.id || column.accessorKey),
      })),
      grouping: [],
      sorting: {
        key: 'createdDate',
        direction: 'desc',
      },
    },
  },
  {
    handle: MODULE.Repairs,
    table: {
      columnOrder: repairsColumns.map((column) => ({
        id: column.id || column.accessorKey,
        active: defaultActiveColumns[MODULE.Repairs].includes(column.id || column.accessorKey),
      })),
      grouping: [],
      sorting: {
        key: 'createdDate',
        direction: 'desc',
      },
    },
  },
  {
    handle: MODULE.Issues,
    table: {
      columnOrder: createIssueTableColumns().map((column) => ({
        id: column.id || column.accessorKey,
        active: defaultActiveColumns[MODULE.Issues].includes(column.id || column.accessorKey),
      })),
      grouping: ['asset'],
      sorting: {
        key: 'severity',
        direction: 'desc',
      },
    },
  },
  {
    handle: MODULE.RepairForms,
    table: {
      columnOrder: repairFormsColumns.map((column) => ({
        id: column.id || column.accessorKey,
        active: defaultActiveColumns[MODULE.RepairForms].includes(column.id || column.accessorKey),
      })),
      grouping: [],
      sorting: {},
    },
    view: 'grid',
  },
  {
    handle: MODULE.RepairSubmissions,
    table: {
      columnOrder: repairSubmissionsColumns().map((column) => ({
        id: column.id || column.accessorKey,
        active: defaultActiveColumns[MODULE.RepairSubmissions].includes(column.id || column.accessorKey),
      })),
      grouping: [],
      sorting: {
        key: 'createdDate',
        direction: 'desc',
      },
    },
  },
  {
    handle: MODULE.ScheduleFormSubmissions,
    table: {
      columnOrder: scheduleFormColumns().map((column) => ({
        id: column.id || column.accessorKey,
        active: defaultActiveColumns[MODULE.ScheduleFormSubmissions].includes(column.id || column.accessorKey),
      })),
      grouping: [],
      sorting: {},
    },
    view: 'grid',
  },
  {
    handle: MODULE.Schedules,
    table: {
      columnOrder: createSchedulesTableColumns().map((column) => ({
        id: column.id || column.accessorKey,
        active: defaultActiveColumns[MODULE.Schedules].includes(column.id || column.accessorKey),
      })),
      grouping: [],
      sorting: {},
    },
    view: 'grid',
  },
  {
    handle: MODULE.ScheduleFormForms,
    table: {
      columnOrder: createScheduleFormFormsTableColumns().map((column) => ({
        id: column.id || column.accessorKey,
        active: defaultActiveColumns[MODULE.ScheduleFormForms].includes(column.id || column.accessorKey),
      })),
      grouping: [],
      sorting: {},
    },
  },
  {
    handle: MODULE.ScheduleFormFormSubmissions,
    table: {
      columnOrder: scheduleFormSubmissionsColumns().map((column) => ({
        id: column.id || column.accessorKey,
        active: defaultActiveColumns[MODULE.ScheduleFormFormSubmissions].includes(column.id || column.accessorKey),
      })),
      grouping: [],
      sorting: {},
    },
  },
  {
    handle: MODULE.DeploymentRules,
    table: {
      columnOrder: createDeploymentTableColumns().map((column) => ({
        id: column.id || column.accessorKey,
        active: defaultActiveColumns[MODULE.DeploymentRules].includes(column.id || column.accessorKey),
      })),
      grouping: [],
      sorting: {
        key: 'createdAt',
        direction: 'desc',
      },
    },
  },
  {
    handle: MODULE.GearbeltContributors,
    table: {
      columnOrder: createGearbeltContributorsTableColumns().map((column) => ({
        id: column.id || column.accessorKey,
        active: defaultActiveColumns[MODULE.GearbeltContributors].includes(column.id || column.accessorKey),
      })),
      grouping: [],
      sorting: {
        key: 'createdAt',
        direction: 'desc',
      },
    },
  },
  {
    handle: MODULE.SitemateUsers,
    table: {
      columnOrder: createSitemateUsersTableColumns().map((column) => ({
        id: column.id || column.accessorKey,
        active: defaultActiveColumns[MODULE.SitemateUsers].includes(column.id || column.accessorKey),
      })),
      grouping: [],
      sorting: {
        key: 'firstName',
        direction: 'desc',
      },
    },
  },
  {
    handle: MODULE.AssetDocuments,
    table: {
      columnOrder: assetDocumentColumns.map((column) => ({
        id: column.id || column.accessorKey,
        active: defaultActiveColumns[MODULE.AssetDocuments].includes(column.id || column.accessorKey),
      })),
      grouping: [],
      sorting: {
        key: 'createdAt',
        direction: 'desc',
      },
    },
  },
  {
    handle: MODULE.AssetInspections,
    table: {
      columnOrder: assetInspectionsColumns().map((column) => ({
        id: column.id || column.accessorKey,
        active: defaultActiveColumns[MODULE.AssetInspections].includes(column.id || column.accessorKey),
      })),
      grouping: [],
      sorting: {
        key: 'createdDate',
        direction: 'desc',
      },
    },
  },
  {
    handle: MODULE.AssetRepairs,
    table: {
      columnOrder: assetRepairsColumns.map((column) => ({
        id: column.id || column.accessorKey,
        active: defaultActiveColumns[MODULE.AssetRepairs].includes(column.id || column.accessorKey),
      })),
      grouping: [],
      sorting: {
        key: 'createdDate',
        direction: 'desc',
      },
    },
  },
  {
    handle: MODULE.AssetScheduleRecords,
    table: {
      columnOrder: assetScheduleRecordsColumns().map((column) => ({
        id: column.id || column.accessorKey,
        active: defaultActiveColumns[MODULE.AssetScheduleRecords].includes(column.id || column.accessorKey),
      })),
      grouping: [],
      sorting: {
        key: 'createdDate',
        direction: 'desc',
      },
    },
  },
  {
    handle: MODULE.AssetWorkOrders,
    table: {
      columnOrder: assetWorkOrderColumns.map((column) => ({
        id: column.id || column.accessorKey,
        active: defaultActiveColumns[MODULE.AssetWorkOrders].includes(column.id || column.accessorKey),
      })),
      grouping: [],
      sorting: {
        key: 'dueDate',
        direction: 'desc',
      },
    },
  },
  {
    handle: MODULE.Documents,
    table: {
      columnOrder: createDocumentsTableColumns().map((column) => ({
        id: column.id || column.accessorKey,
        active: defaultActiveColumns[MODULE.Documents].includes(column.id || column.accessorKey),
      })),
      grouping: [],
      sorting: {},
    },
  },
  {
    handle: MODULE.WorkOrders,
    table: {
      columnOrder: workOrdersColumns.map((column) => ({
        id: column.id || column.accessorKey,
        active: defaultActiveColumns[MODULE.WorkOrders].includes(column.id || column.accessorKey),
      })),
      grouping: [],
      sorting: {},
    },
  },
  {
    handle: MODULE.AssetReadingLogs,
    table: {
      columnOrder: assetReadingLogsColumns.map((column) => ({
        id: column.id || column.accessorKey,
        active: defaultActiveColumns[MODULE.AssetReadingLogs].includes(column.id || column.accessorKey),
      })),
      grouping: [],
      sorting: {},
    },
  },
  {
    handle: MODULE.AssetLocationLogs,
    table: {
      columnOrder: assetLocationLogsColumns.map((column) => ({
        id: column.id || column.accessorKey,
        active: defaultActiveColumns[MODULE.AssetLocationLogs].includes(column.id || column.accessorKey),
      })),
      grouping: [],
      sorting: {},
    },
  },
  {
    handle: MODULE.LocationLogs,
    table: {
      columnOrder: locationLogsColumns.map((column) => ({
        id: column.id || column.accessorKey,
        active: defaultActiveColumns[MODULE.LocationLogs].includes(column.id || column.accessorKey),
      })),
      grouping: [],
      sorting: {
        key: 'createdAt',
        direction: 'desc',
      },
    },
  },
  {
    handle: MODULE.AssetIssues,
    table: {
      columnOrder: createAssetIssueTableColumns().map((column) => ({
        id: column.id || column.accessorKey,
        active: defaultActiveColumns[MODULE.AssetIssues].includes(column.id || column.accessorKey),
      })),
      grouping: [],
      sorting: {},
    },
  },
  {
    handle: MODULE.AssetScheduleChecks,
    table: {
      columnOrder: createAssetScheduleCheckColumns().map((column) => ({
        id: column.id || column.accessorKey,
        active: defaultActiveColumns[MODULE.AssetScheduleChecks].includes(column.id || column.accessorKey),
      })),
      grouping: [],
      sorting: {
        key: 'dueProgress',
        direction: 'desc',
      },
      filters: [{ id: 'status', value: ['open'] }],
    },
  },
  {
    handle: MODULE.ScheduleChecks,
    table: {
      columnOrder: createCheckTableColumns().map((column) => ({
        id: column.id || column.accessorKey,
        active: defaultActiveColumns[MODULE.ScheduleChecks].includes(column.id || column.accessorKey),
      })),
      filters: [{ id: 'status', value: [CheckStatus.Open] }],
      grouping: [],
      sorting: {
        key: 'dueProgress',
        direction: 'desc',
      },
    },
  },
];

export const useModulesStore = create<ModulesStore>()(
  persist(
    (set, get) => ({
      modules: defaultModules,
      getModule: (handle: MODULE) => {
        const module =
          get().modules.find((m) => m.handle === handle) ??
          (defaultModules.find((m) => m.handle === handle) &&
            set((state) => ({ modules: [...state.modules, defaultModules.find((m) => m.handle === handle)!] })));

        if (!module) throw new Error(`Module ${handle} not found`);
        return module;
      },
      setModule: (handle: MODULE, update: Partial<Omit<Module, 'handle'>>) =>
        set((state) => ({
          modules: state.modules.map((m) => (m.handle === handle ? { ...m, ...update } : m)),
        })),
      setModuleColumns: (handle: MODULE, columnOrder: Array<ModuleTableColumn>) =>
        set((state) => ({
          modules: state.modules.map((m) => (m.handle === handle ? { ...m, table: { ...m.table, columnOrder } } : m)),
        })),
      setModuleSorting: (handle: MODULE, sorting: { key?: string; direction?: 'asc' | 'desc' }) =>
        set((state) => ({
          modules: state.modules.map((m) => (m.handle === handle ? { ...m, table: { ...m.table, sorting } } : m)),
        })),
      setModuleGrouping: (handle: MODULE, grouping: string[]) =>
        set((state) => ({
          modules: state.modules.map((m) => (m.handle === handle ? { ...m, table: { ...m.table, grouping } } : m)),
        })),
      setModuleFilters: (handle: MODULE, filters: ColumnFiltersState) =>
        set((state) => ({
          modules: state.modules.map((m) => (m.handle === handle ? { ...m, table: { ...m.table, filters } } : m)),
        })),
      setModuleView: (handle: MODULE, view: 'grid' | 'register') =>
        set((state) => ({
          modules: state.modules.map((m) => (m.handle === handle ? { ...m, view } : m)),
        })),
      getColumnVisibility: (handle: MODULE) => {
        const module = get().getModule(handle);

        return module.table.columnOrder.reduce((acc, col) => {
          acc[col.id] = col.active;
          return acc;
        }, {} as VisibilityState);
      },
      syncModuleColumns: <T>(handle: MODULE, columns: ColumnDef<T>[]) => {
        {
          /* Temp solution for accessorKey not being typed correctly: https://github.com/TanStack/table/issues/4423 */
        }
        const retypedColumns = columns as (ColumnDef<T> & { accessorKey?: string })[];

        const module = get().getModule(handle);
        const currentColumnOrder = module.table.columnOrder;

        if (!currentColumnOrder) {
          set((state) => ({
            modules: state.modules.map((m) =>
              m.handle === handle
                ? {
                    ...m,
                    table: {
                      ...m.table,
                      columnOrder: createAssetTableColumns().map((column) => ({
                        id: column.id || column.accessorKey,
                        active: defaultActiveColumns[handle].includes(column.id || column.accessorKey),
                      })),
                    },
                  }
                : m,
            ),
          }));
          return;
        }

        // Get all column IDs from table-meta as source of truth
        const metaColumnIds = new Set(retypedColumns.map((col) => col.id || col.accessorKey));
        const currentIds = new Set(currentColumnOrder.map((col) => col.id));

        // Remove columns that don't exist in meta
        const filteredColumns = currentColumnOrder.filter((col) => metaColumnIds.has(col.id));

        // Add any new columns from meta that don't exist in current
        const columnsToAdd = retypedColumns
          .filter((col) => !currentIds.has(col.id || col.accessorKey || ''))
          .map((col) => ({
            id: col.id || col.accessorKey || '',
            active: false,
          }));

        set((state) => ({
          modules: state.modules.map((m) => (m.handle === handle ? { ...m, table: { ...m.table, columnOrder: [...filteredColumns, ...columnsToAdd] } } : m)),
        }));
      },
    }),
    {
      name: 'gb-modules',
      storage: createJSONStorage(() => localStorage),
    },
  ),
);
useModulesStore.setState((state) => ({
  ...state,
  modules: state.modules.map((m) => ({
    ...m,
    // If columnOrder is not set, use the default columns for the module
    table: { ...m.table, columnOrder: m.table.columnOrder ?? (defaultModules.find((m) => m.handle === m.handle)?.table.columnOrder || []) },
  })),
}));
